import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Per-user view preferences that outlive the session.
 *
 * Scope is deliberately narrow: which view you were last looking at, and how
 * you like it displayed. Never data, never anything the URL should own for a
 * single visit, and never anything a colleague opening a shared link should
 * inherit.
 *
 * Storage is localStorage, keyed by the signed-in user, which is per browser
 * rather than per person — the same account on another machine starts fresh.
 * The alternative for the nerdpack is NerdStorage, which does follow the user,
 * but it exists only inside New Relic and this code has to run in both targets.
 * The key is scoped to the user anyway, so a shared browser does not hand one
 * person's last market to the next.
 */

const PreferencesContext = createContext(null);

const NAMESPACE = "healthDashboard.prefs";
const VERSION = 1;

const storageKey = (userKey) =>
  `${NAMESPACE}.v${VERSION}.${userKey || "anonymous"}`;

/**
 * localStorage is not always there to be used: Safari's private mode throws on
 * write, some managed browsers disable it, and an iframe with a restrictive
 * sandbox has no access at all. A preference is never worth a crash, so every
 * access is guarded and the whole thing degrades to in-memory.
 */
const readStore = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.warn("[Preferences] Could not read stored preferences:", error);
    return {};
  }
};

const writeStore = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn("[Preferences] Could not persist preferences:", error);
    return false;
  }
};

export const PreferencesProvider = ({ userKey, children }) => {
  const key = storageKey(userKey);

  // Read once per user. Re-reading on every render would make a hook that
  // reads a preference re-render on every write to any other preference.
  const [values, setValues] = useState(() => readStore(key));
  const loadedFor = useRef(key);

  // A different user signed in: swap to their preferences rather than writing
  // one person's choices under the other's key.
  if (loadedFor.current !== key) {
    loadedFor.current = key;
    setValues(readStore(key));
  }

  const set = useCallback(
    (name, value) => {
      setValues((previous) => {
        if (previous[name] === value) return previous;
        const next = { ...previous, [name]: value };
        writeStore(key, next);
        return next;
      });
    },
    [key]
  );

  const value = useMemo(() => ({ values, set }), [values, set]);

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

/**
 * Reads a stored preference. Returns the fallback when nothing is stored, so a
 * first visit behaves exactly as it did before any of this existed.
 *
 * Safe outside the provider: components that render in isolation (tests, the
 * entity panel) get the fallback and a no-op setter rather than throwing.
 */
export const usePreference = (name, fallback) => {
  const context = useContext(PreferencesContext);
  const stored = context?.values?.[name];
  const set = context?.set;

  const setPreference = useCallback(
    (value) => set?.(name, value),
    [set, name]
  );

  return [stored === undefined ? fallback : stored, setPreference];
};

/** The whole bag, for callers that need to read several at once. */
export const usePreferences = () => useContext(PreferencesContext);

export default PreferencesContext;
